// cloud-admin API client — talks to cloud-api's /api/v1/system/* control plane.
// The system JWT is kept in memory + localStorage (a single-operator console),
// attached as Bearer on every request.

import axios from 'axios';

const TOKEN_KEY = 'iosentra_system_token';
let token: string | null = localStorage.getItem(TOKEN_KEY);

export function setToken(t: string | null) {
  token = t;
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}
export function getToken() { return token; }

export const api = axios.create({ baseURL: import.meta.env.VITE_API_URL || '', withCredentials: true });
api.interceptors.request.use((cfg) => {
  cfg.headers = cfg.headers ?? {};
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// ── Types ───────────────────────────────────────────────────────────────────
export interface SystemUser { id: string; email: string; name: string; roles: string[] }
export interface Tenant {
  id: string; name: string; slug: string; status: string; tier: string;
  region: string | null;
  databaseName: string; logtoOrgId: string | null; createdAt: string;
}
export interface ProvisionResult {
  tenantId: string; databaseName: string; logtoOrgId: string | null; status: string; steps: string[];
}
export interface FleetRollup {
  tenants: number; activeTenants: number; totalDevices: number;
  perTenant: Array<{ id: string; name: string; status: string; devices: number }>;
}
export interface FleetAgent {
  edgeId: string; tenantId: string; tenantName: string | null;
  name: string; status: string; connected: boolean;
  agentVersion: string | null; versionStale: boolean;
  lastSeenAt: string | null;
  pairingState: 'paired' | 'awaiting_pairing' | 'code_expired' | 'none';
}
export interface AgentLogEntry {
  id: string; level: string; module: string | null; message: string;
  eventTime: string | null; receivedAt: string; metadata: Record<string, unknown>;
}
export interface AuditEntry {
  id: string; tenantId: string | null; action: string; resourceType: string | null;
  resourceId: string | null; details: Record<string, unknown>; createdAt: string;
  actor: string | null; actorType: string | null;
}
export interface Usage { devices: number; cameras: number; edges: number; users: number }
export interface Subscription { plan: string; status: string; limits: Record<string, number>; priceCents: number | null; currency: string; planId: string | null; cardTokenId: string | null; cardLast4: string | null; lastChargedAt: string | null; lastChargeStatus: string | null; trialEndsAt: string | null; renewsAt: string | null }
export interface ApiKey { id: string; name: string; keyPrefix: string; revokedAt: string | null; lastUsedAt: string | null; createdAt: string }
export interface TenantDetail {
  tenant: { id: string; name: string; slug: string; status: string; tier: string; tenantType: string; customDomain: string | null; region: string | null; databaseName: string; logtoOrgId: string | null; customerCode: string | null; suspendedAt: string | null; createdAt: string };
  subscription: Subscription | null;
  usage: Usage; overLimit: string[];
  featureFlags: Record<string, boolean>;
  /** Which flags the runtime actually enforces (CFG-04) — the rest are inert. */
  flagStatus?: Record<string, { enforced: boolean; gates: string }>;
  apiKeys: ApiKey[];
  recentAudit: Array<{ id: string; action: string; resourceType: string | null; createdAt: string }>;
}
export interface TenantUser { id: string; email: string; name: string; role: string; suspended: boolean }
export interface Invoice { id: string; periodStart: string; periodEnd: string; amountCents: number | null; currency: string; status: string; number: string | null; externalId: string | null; externalUrl: string | null; usageData: Record<string, unknown>; createdAt: string }
export interface Plan {
  id: string; code: string; name: string; isActive: boolean; currency: string;
  billingInterval: 'monthly' | 'yearly'; basePriceCents: number;
  includedCameras: number | null; includedDevices: number | null;
  perCameraCents: number | null; perDeviceCents: number | null; sortOrder: number;
}
export interface HealthService { service: string; status: 'ok' | 'down' | 'not_deployed' | 'not_configured' | 'unknown'; detail: string; required?: boolean }
export interface OverviewData {
  tenants: number;
  byStatus: { active: number; provisioning: number; error: number; suspended: number; other: number };
  totalDevices: number; totalCameras: number;
  mrrCents: number;
  trialsActive: number; trialsExpiringSoon: number;
  overLimitTenants: number;
  perTenant: Array<{ id: string; name: string; status: string; devices: number; cameras: number; plan: string | null; subStatus: string | null; priceCents: number; over: boolean }>;
}
export interface MetricsPoint {
  t: string; tenants: number; activeTenants: number;
  totalDevices: number; totalCameras: number; edgesConnected: number; mrrCents: number;
}
export interface MetricsHistory { range: string; points: MetricsPoint[] }

// ── Calls ─────────────────────────────────────────────────────────────────
export const systemApi = {
  // 200 → { accessToken, user }; 202 → a 2FA step (verify a code, or enroll first).
  login: async (email: string, password: string) =>
    (await api.post<{ accessToken?: string; user?: SystemUser; requiresTotp?: boolean; requiresTotpEnrollment?: boolean; challenge?: string }>('/api/v1/system/login', { email, password })).data,
  // Complete a login where the user already has TOTP.
  loginVerifyTotp: async (challenge: string, code: string) =>
    (await api.post<{ accessToken: string; user: SystemUser }>('/api/v1/system/login/verify-totp', { challenge, code })).data,
  // Forced-enrollment: start (needs the enroll challenge as bearer) → confirm
  // (returns the full session). The bearer is passed explicitly since there's no
  // session token yet.
  enrollChallengeStart: async (challenge: string) =>
    (await api.post<{ data: { secret: string; otpauth: string; qrDataUrl: string } }>('/api/v1/system/security/totp/enroll', {}, { headers: { authorization: `Bearer ${challenge}` } })).data.data,
  enrollChallengeConfirm: async (challenge: string, code: string) =>
    (await api.post<{ data: { enabled: boolean; accessToken?: string; user?: SystemUser } }>('/api/v1/system/security/totp/confirm', { code }, { headers: { authorization: `Bearer ${challenge}` } })).data.data,
  me: async () => (await api.get<{ data: SystemUser }>('/api/v1/system/me')).data.data,
  tenants: async () => (await api.get<{ data: Tenant[] }>('/api/v1/system/tenants')).data.data,
  regions: async () => (await api.get<{ data: { enabled: boolean; regions: string[] } }>('/api/v1/system/regions')).data.data,
  provision: async (input: {
    name: string; slug: string; tier?: string; tenantType?: string; region?: string;
    trialDays?: number; trialPlan?: string;
    adminEmail: string; adminPassword: string; adminName?: string;
  }) => (await api.post<{ data: ProvisionResult }>('/api/v1/system/tenants', input)).data.data,
  retryTenant: async (id: string) => (await api.post<{ data: ProvisionResult }>(`/api/v1/system/tenants/${id}/retry`, {})).data.data,
  deleteTenant: async (id: string, force = false) =>
    (await api.delete<{ data: { deleted: boolean } }>(`/api/v1/system/tenants/${id}${force ? '?force=true' : ''}`)).data.data,
  fleet: async () => (await api.get<{ data: FleetRollup }>('/api/v1/system/fleet')).data.data,
  agents: async () => (await api.get<{ data: FleetAgent[] }>('/api/v1/system/edges')).data.data,
  agentLogs: async (edgeId: string, limit = 200) =>
    (await api.get<{ data: AgentLogEntry[] }>(`/api/v1/system/edges/${edgeId}/logs?limit=${limit}`)).data.data,
  overview: async () => (await api.get<{ data: OverviewData }>('/api/v1/system/overview')).data.data,
  metricsHistory: async (range: '24h' | '7d' | '30d' = '24h') =>
    (await api.get<{ data: MetricsHistory }>(`/api/v1/system/metrics/history?range=${range}`)).data.data,
  audit: async () => (await api.get<{ data: AuditEntry[] }>('/api/v1/system/audit')).data.data,

  // ── Tenant detail + management ──
  tenantDetail: async (id: string) => (await api.get<{ data: TenantDetail }>(`/api/v1/system/tenants/${id}`)).data.data,
  editTenant: async (id: string, patch: Partial<{ name: string; tier: string; tenantType: string; customDomain: string; region: string }>) =>
    (await api.patch(`/api/v1/system/tenants/${id}`, patch)).data,
  suspend: async (id: string) => (await api.post(`/api/v1/system/tenants/${id}/suspend`, {})).data,
  unsuspend: async (id: string) => (await api.post(`/api/v1/system/tenants/${id}/unsuspend`, {})).data,
  setFlags: async (id: string, flags: Record<string, boolean>) => (await api.put(`/api/v1/system/tenants/${id}/flags`, { flags })).data,
  setSubscription: async (id: string, sub: Partial<Subscription>) => (await api.put(`/api/v1/system/tenants/${id}/subscription`, sub)).data,

  // ── Users per tenant ──
  tenantUsers: async (id: string) => (await api.get<{ data: TenantUser[] }>(`/api/v1/system/tenants/${id}/users`)).data.data,
  inviteAdmin: async (id: string, input: { email: string; name?: string; role?: string }) =>
    (await api.post<{ data: { userId: string; email: string; role: string; created: boolean; emailSent?: boolean } }>(`/api/v1/system/tenants/${id}/admin`, input)).data.data,
  setUserRole: async (id: string, userId: string, role: string) => (await api.patch(`/api/v1/system/tenants/${id}/users/${userId}`, { role })).data,
  removeUser: async (id: string, userId: string) => (await api.delete(`/api/v1/system/tenants/${id}/users/${userId}`)).data,
  // GDPR/PPL data-subject rights:
  exportUser: async (id: string, userId: string) => (await api.get(`/api/v1/system/tenants/${id}/users/${userId}/export`)).data,
  eraseUser: async (id: string, userId: string) => (await api.delete(`/api/v1/system/tenants/${id}/users/${userId}/erase`)).data,

  // ── API keys ──
  apiKeys: async (id: string) => (await api.get<{ data: ApiKey[] }>(`/api/v1/system/tenants/${id}/api-keys`)).data.data,
  createApiKey: async (id: string, name: string) => (await api.post<{ data: { name: string; key: string; keyPrefix: string } }>(`/api/v1/system/tenants/${id}/api-keys`, { name })).data.data,
  revokeApiKey: async (id: string, keyId: string) => (await api.delete(`/api/v1/system/tenants/${id}/api-keys/${keyId}`)).data,

  // ── Billing ──
  billingConfig: async () => (await api.get<{ data: { provider: string; isEnabled: boolean; configured: boolean; baseUrl: string | null } }>('/api/v1/system/billing/config')).data.data,
  saveBillingConfig: async (input: { apiId?: string; apiSecret?: string; baseUrl?: string; isEnabled?: boolean }) => (await api.put('/api/v1/system/billing/config', input)).data,
  testBilling: async () => (await api.post<{ data: { ok: boolean; notConfigured?: boolean; sink?: boolean; error?: string } }>('/api/v1/system/billing/config/test', {})).data.data,
  invoices: async (id: string) => (await api.get<{ data: Invoice[] }>(`/api/v1/system/tenants/${id}/invoices`)).data.data,
  generateInvoice: async (id: string) => (await api.post<{ data: { amountCents: number; sink?: boolean; externalId?: string } }>(`/api/v1/system/tenants/${id}/invoices/generate`, {})).data.data,
  // Plans / pricing catalog
  plans: async () => (await api.get<{ data: Plan[] }>('/api/v1/system/plans')).data.data,
  createPlan: async (input: Partial<Plan>) => (await api.post<{ data: Plan }>('/api/v1/system/plans', input)).data.data,
  updatePlan: async (id: string, input: Partial<Plan>) => (await api.put<{ data: Plan }>(`/api/v1/system/plans/${id}`, input)).data.data,
  deletePlan: async (id: string) => (await api.delete(`/api/v1/system/plans/${id}`)).data,
  // Payments (recurring)
  paymentForm: async (id: string) => (await api.post<{ data: { url: string; sink?: boolean } }>(`/api/v1/system/tenants/${id}/payment-form`, {})).data.data,
  setCardToken: async (id: string, input: { cardTokenId: string; cardLast4?: string }) => (await api.post(`/api/v1/system/tenants/${id}/card-token`, input)).data,
  chargeNow: async (id: string) => (await api.post<{ data: { ok: boolean; chargeStatus?: string; amountCents: number; sink?: boolean } }>(`/api/v1/system/tenants/${id}/charge`, {})).data.data,

  // ── Services + support ──
  healthDeep: async () => (await api.get<{ data: HealthService[] }>('/api/v1/system/health-deep')).data.data,
  impersonate: async (id: string, reason: string) => (await api.post<{ data: { accessToken: string; expiresIn: number; tenant: { id: string; name: string } } }>(`/api/v1/system/tenants/${id}/impersonate`, { reason })).data.data,
  auditExportUrl: (format: 'csv' | 'json' = 'csv', q: { action?: string; tenantId?: string; from?: string; to?: string } = {}) => {
    const p = new URLSearchParams({ format, ...Object.fromEntries(Object.entries(q).filter(([, v]) => v)) });
    return `${import.meta.env.VITE_API_URL || ''}/api/v1/system/audit/export?${p}`;
  },
  // Filtered audit fetch (uses the export endpoint's JSON form — supports
  // action / tenantId / from / to, up to 5000 rows).
  auditFiltered: async (q: { action?: string; tenantId?: string; from?: string; to?: string } = {}) => {
    const params = Object.fromEntries(Object.entries(q).filter(([, v]) => v));
    return (await api.get<{ data: FilteredAuditEntry[] }>('/api/v1/system/audit/export', { params: { format: 'json', ...params } })).data.data;
  },

  // ── Security ──
  security: async () => (await api.get<{ data: SecurityState }>('/api/v1/system/security')).data.data,
  saveSecurity: async (s: Partial<SecuritySettings>) => (await api.put('/api/v1/system/security', s)).data,
  totpEnroll: async () => (await api.post<{ data: { secret: string; otpauth: string; qrDataUrl: string } }>('/api/v1/system/security/totp/enroll', {})).data.data,
  totpConfirm: async (code: string) => (await api.post('/api/v1/system/security/totp/confirm', { code })).data,
  totpDisable: async () => (await api.post('/api/v1/system/security/totp/disable', {})).data,

  // ── Platform-global messaging (providers + templates) ──
  messagingProviders: async () => (await api.get<{ data: MessagingProvidersState }>('/api/v1/system/messaging/providers')).data.data,
  saveMessagingProvider: async (type: string, input: { name?: string; isEnabled?: boolean; config?: Record<string, unknown> }) =>
    (await api.put(`/api/v1/system/messaging/providers/${type}`, input)).data,
  deleteMessagingProvider: async (type: string) => (await api.delete(`/api/v1/system/messaging/providers/${type}`)).data,
  testMessagingProvider: async (type: string, target?: string) =>
    (await api.post<{ data: { ok: boolean; error?: string } }>(`/api/v1/system/messaging/providers/${type}/test`, { target })).data.data,
  messagingTemplates: async (locale = 'en') => (await api.get<{ data: MessagingTemplatesState }>(`/api/v1/system/messaging/templates?locale=${encodeURIComponent(locale)}`)).data.data,
  saveMessagingTemplate: async (eventKind: string, channels: Record<string, Record<string, unknown>>, locale = 'en') =>
    (await api.put(`/api/v1/system/messaging/templates/${eventKind}`, { channels, locale })).data,
  resetMessagingTemplate: async (eventKind: string, locale = 'en') =>
    (await api.delete(`/api/v1/system/messaging/templates/${eventKind}?locale=${encodeURIComponent(locale)}`)).data,
  previewMessagingTemplate: async (eventKind: string, channels: Record<string, { subject?: string; body?: string; text?: string; isHtml?: boolean }>, locale = 'en') =>
    (await api.post<{ data: { rendered: Record<string, { subject?: string; body?: string; text?: string; isHtml?: boolean }> } }>(`/api/v1/system/messaging/templates/${eventKind}/preview`, { channels, locale })).data.data,
};

export interface MessagingProvidersState {
  channels: string[];
  providers: Array<{ type: string; name: string | null; isEnabled: boolean; config: Record<string, unknown>; messagesSent: number; messagesFailed: number; lastUsedAt: string | null; lastError: string | null }>;
}
export interface MessagingTemplatesState {
  eventKinds: string[];
  channels: string[];
  variables: Array<{ group: string; vars: Array<{ token: string; example: string }> }>;
  locale: string;
  languages: string[];
  templates: Array<{ eventKind: string; channels: Record<string, { subject?: string; body?: string; text?: string; isHtml?: boolean }>; isActive: boolean }>;
}

export interface FilteredAuditEntry {
  id: string; action: string; actorId: string | null; actor: string | null; actorType: string | null;
  resourceType: string | null; resourceId: string | null; tenantId: string | null; tenant: string | null;
  details: Record<string, unknown> | null; ip: string | null; createdAt: string;
}
export interface SecuritySettings {
  twoFactorRequired: boolean; passwordMinLength: number; passwordRequireUpper: boolean;
  passwordRequireNumber: boolean; passwordRequireSymbol: boolean; ipAllowlist: string[];
  ipAllowlistEnabled: boolean; sessionTimeoutSeconds: number; tenantMfaEnforced: boolean;
}
export interface SecurityState { enforcementEnabled: boolean; settings: SecuritySettings; myTotpEnabled: boolean }
